import { toast } from "sonner";
import { download, importStudent, upload } from "../../asset";
import DocumentUpload from "../../components/common/DocumetUpload";
import CustomButton from "../../components/CustomButton";
import { exportToExcel } from "../../util/functions/UniversalFunctions";
import QueryApis from "../../api/QueryApis";
import { useState } from "react";
import * as XLSX from "xlsx";

const QUERY_TEMPLATE = [
  {
    studentName: "",
    dateOfBirth: "",
    placeOfBirth: "",
    motherEmail: "",
    motherPhone: "",
    fatherEmail: "",
    fatherPhone: "",
    remarks: "",
    sourceOfInfo: "",
  },
];

export default function QueriesImport() {
  const [fileData, setFileData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const handleDownloadTemplate = () => {
    exportToExcel(QUERY_TEMPLATE, "Queries_Import_Template");
  };

  const excelDateToJSDate = (excelDate: number) => {
    const jsDate = new Date((excelDate - 25569) * 86400 * 1000);
    return jsDate.toISOString().split("T")[0]; // YYYY-MM-DD
  };

  const handleFileSelect = (file: File) => {
    const reader = new FileReader();

    reader.onload = (event: any) => {
      const workbook = XLSX.read(event.target.result, { type: "binary" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData: any[] = XLSX.utils.sheet_to_json(sheet);

      if (!jsonData.length) {
        toast.error("Excel file is empty");
        return;
      }

      const formattedData = jsonData.map((row) => ({
        ...row,
        dateOfBirth:
          typeof row.dateOfBirth === "number"
            ? excelDateToJSDate(row.dateOfBirth)
            : row.dateOfBirth,
      }));

      setFileData(formattedData);
      toast.success(`${formattedData.length} records loaded`);
    };

    reader.readAsBinaryString(file);
  };

  const handleUpload = async () => {
    if (!fileData.length) {
      toast.error("Please select a file first");
      return;
    }

    try {
      setLoading(true);
      toast.loading("Uploading queries...", { id: "bulk" });

      const results = await Promise.allSettled(
        fileData.map((row) => QueryApis.createQuery(row)),
      );

      const successCount = results.filter(
        (r) => r.status === "fulfilled",
      ).length;

      const failedCount = results.filter((r) => r.status === "rejected").length;

      toast.success(
        `Upload complete: ${successCount} success, ${failedCount} failed`,
        { id: "bulk" },
      );

      setFileData([]);
    } catch (error) {
      toast.error("Bulk upload failed", { id: "bulk" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid gap-4">
      <div className="gap-14 bg-white px-6 py-7 rounded-lg grid place-content-center">
        {/* Header */}
        <div className="flex flex-col gap-3 justify-center items-center text-center">
          <span className="text-neutral-300 text-sm font-semibold">
            How it works?
          </span>
          <h3 className="text-primary-600 text-[28px] font-semibold">
            Upload bulk Queries data in just 3 easy steps
          </h3>
        </div>

        <div className="flex flex-col lg:flex-row justify-between items-center gap-10 w-full">
          <div className="bg-success-50 p-10 lg:p-16 rounded-lg w-full lg:max-w-96 flex items-center justify-center h-full">
            <img
              src={importStudent}
              alt="Upload bulk student data"
              className="w-full h-auto max-h-[400px] object-contain"
            />
          </div>

          {/* Steps */}
          <div className="flex flex-col gap-10 w-full lg:h-full justify-between">
            {/* STEP 1 */}
            <div className="flex gap-6 items-start flex-wrap sm:flex-nowrap">
              <div className="bg-neutral-50 rounded px-3 p-1 flex flex-col items-center text-sm text-neutral-300 font-medium">
                <span>STEP</span>
                <span>1</span>
              </div>

              <div className="text-neutral-300 flex flex-col gap-4">
                <div>
                  <p className="text-xl font-semibold">
                    Download Excel Template
                  </p>
                  <p className="text-sm">
                    Start by downloading the Excel template to easily enter all
                    Queries in one place.
                  </p>
                </div>

                <CustomButton
                  text="Download Template"
                  color="bg-primary-500"
                  onClick={handleDownloadTemplate}
                  icon={download}
                />
              </div>
            </div>

            {/* STEP 2 */}
            <div className="flex gap-6 items-start flex-wrap sm:flex-nowrap">
              <div className="bg-neutral-50 rounded px-3 p-1 flex flex-col items-center text-sm text-neutral-300 font-medium">
                <span>STEP</span>
                <span>2</span>
              </div>

              <div className="text-neutral-300 flex flex-col gap-4">
                <div>
                  <p className="text-xl font-semibold">Choose Excel File</p>
                  <p className="text-sm">
                    Next, upload the Excel sheet you’ve filled out with the
                    Queries to proceed.
                  </p>
                </div>

                <DocumentUpload
                  onFileSelect={handleFileSelect}
                  showDropzone={false}
                  buttonText="Choose File"
                  buttonColor="bg-primary-500"
                  accept=".xls,.xlsx"
                  buttonWidth="fit"
                  className="flex justify-start items-start"
                />
              </div>
            </div>

            {/* STEP 3 */}
            <div className="flex gap-6 items-start flex-wrap sm:flex-nowrap">
              <div className="bg-neutral-50 rounded px-3 p-1 flex flex-col items-center text-sm text-neutral-300 font-medium">
                <span>STEP</span>
                <span>3</span>
              </div>

              <div className="text-neutral-300 flex flex-col gap-4">
                <div>
                  <button className="text-xl font-semibold">Upload File</button>
                  <p className="text-sm">
                    Sit back while we automatically process the file and
                    generate your complete Queries.
                  </p>
                </div>

                <CustomButton
                  text={loading ? "Uploading..." : "Upload"}
                  color="bg-primary-500"
                  icon={upload}
                  onClick={handleUpload}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
